import { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { Separator } from '@ui/separator.tsx';
import { toast } from 'sonner';
import { EyeIcon, EyeOffIcon, PencilIcon, FileTextIcon } from 'lucide-react';

import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';
import {
  apiMediasPost,
  adminWorkspaceConfigs,
  apiAdminworkspaceConfigsIdPatch,
  apiAdminworkspaceConfigsPost
} from '@r/api/generated/client.ts';
import type {
  WorkspaceConfigWorkspaceConfigReadModel,
  ConfigConfigReadModel
} from '@r/api/generated/client.schemas.ts';
import { useAllConfigs, getConfigsFromResponse, findConfig } from '@r/hooks/use-all-configs.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type LegalKey = 'regulation' | 'privacyPolicy' | 'legalNotice';

interface LegalDocumentBlockProps {
  label: string;
  description: string;
  expectedFormat: string;
  currentUrl: string | null;
  registerName: LegalKey;
  canEdit: boolean;
  onFileChange: (key: LegalKey, file: File | undefined) => void;
}

const LegalDocumentBlock = ({
  label,
  description,
  expectedFormat,
  currentUrl,
  registerName,
  canEdit,
  onFileChange
}: LegalDocumentBlockProps) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const fileName = currentUrl
    ? ((currentUrl.split('/').pop() ?? currentUrl).split('?')[0] ?? '')
    : '';

  return (
    <section className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{label}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <p className="text-sm text-muted-foreground">{expectedFormat}</p>

      <div className="p-4 rounded-lg border bg-muted/40">
        {currentUrl ? (
          <div className="flex gap-4 justify-between items-center">
            <div className="flex gap-3 items-center min-w-0">
              <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-background">
                <FileTextIcon className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-medium truncate" title={fileName}>
                  {fileName}
                </p>
              </div>
            </div>
            <div className="flex gap-1 items-center shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="w-8 h-8 rounded-full"
                type="button"
                onClick={() => setIsPreviewOpen((prev) => !prev)}
                aria-label={
                  isPreviewOpen
                    ? t('admin.legal.documents.actions.hide')
                    : t('admin.legal.documents.actions.view')
                }
              >
                {isPreviewOpen ? (
                  <EyeOffIcon className="w-4 h-4" />
                ) : (
                  <EyeIcon className="w-4 h-4" />
                )}
              </Button>
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="w-8 h-8 rounded-full"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label={t('admin.legal.documents.actions.edit')}
                >
                  <PencilIcon className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{t('admin.legal.documents.empty')}</p>
            {!canEdit && (
              <p className="text-sm text-muted-foreground">
                {t('admin.legal.documents.permissions.read.only')}
              </p>
            )}
          </div>
        )}
      </div>

      {isPreviewOpen && currentUrl && (
        <div className="overflow-hidden mt-4 rounded-lg border bg-muted/40">
          <iframe
            src={currentUrl}
            title={fileName}
            className="w-full h-[500px] border-0"
            loading="lazy"
          />
        </div>
      )}

      {canEdit && (
        <Input
          ref={fileInputRef}
          type="file"
          accept="application/pdf"
          onChange={(event) => onFileChange(registerName, event.target.files?.[0])}
          aria-label={label}
          className="hidden"
        />
      )}
    </section>
  );
};

export const LegalMediasPage = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();
  const user = useUser().data;
  const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
  const roles = activeWorkspace?.roles ?? [];
  const canEditLegalDocs = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');

  // Configs disponibles (dont les configs media 'regulation', 'privacy-policy', 'legal-notice')
  const configsQuery = useAllConfigs();
  const configs = useMemo<ConfigConfigReadModel[] | undefined>(
    () => getConfigsFromResponse(configsQuery.data),
    [configsQuery.data]
  );

  const workspaceConfigsQuery = useQuery<WorkspaceConfigWorkspaceConfigReadModel[]>({
    queryKey: ['workspaceConfigs', 'legal-medias'],
    queryFn: async () => {
      const response = await adminWorkspaceConfigs({ 'config.type': 'media' });
      return (response.data || []) as WorkspaceConfigWorkspaceConfigReadModel[];
    }
  });

  const regulationUrl = useGetConfigByType('media', 'regulation')?.[0] ?? null;
  const privacyPolicyUrl = useGetConfigByType('media', 'privacy-policy')?.[0] ?? null;
  const legalNoticeUrl = useGetConfigByType('media', 'legal-notice')?.[0] ?? null;

  const getMediaConfigForKey = (key: LegalKey): ConfigConfigReadModel | undefined => {
    if (!configs) return undefined;
    const configKey =
      key === 'regulation'
        ? 'regulation'
        : key === 'privacyPolicy'
          ? 'privacy-policy'
          : 'legal-notice';

    return findConfig(configs, 'media', configKey);
  };

  const getWorkspaceConfigForConfigId = (configId?: number) => {
    if (!configId) return undefined;
    return workspaceConfigsQuery.data?.find(
      (workspaceConfig) => workspaceConfig.config?.id === configId
    );
  };

  const handleFileChange = async (key: LegalKey, file: File | undefined) => {
    if (!file) {
      return;
    }
    if (!canEditLegalDocs) {
      toast.error(t('admin.legal.documents.permissions.denied'));
      return;
    }

    const mediaConfig = getMediaConfigForKey(key);
    if (!mediaConfig?.id) {
      toast.error(t('admin.legal.documents.config.not.found', { key }));
      return;
    }

    try {
      const media = await apiMediasPost({ file, type: 'legal' });

      if (!media?.iri) {
        toast.error(t('admin.legal.documents.media.error'));
        return;
      }

      const existingWorkspaceConfig = getWorkspaceConfigForConfigId(mediaConfig.id);

      try {
        if (existingWorkspaceConfig?.id) {
          await apiAdminworkspaceConfigsIdPatch(existingWorkspaceConfig.id.toString(), {
            value: String(media.id),
            media: media.iri
          });
        } else {
          await apiAdminworkspaceConfigsPost({
            value: String(media.id),
            media: media.iri,
            config: `/configs/${mediaConfig.id}`
          });
        }
      } catch (error) {
        if (
          isOrvalErrorFromPath(error, 'patch', '/admin/workspaceConfigs') ||
          isOrvalErrorFromPath(error, 'post', '/admin/workspaceConfigs')
        ) {
          showOrvalErrorToast(error, t('admin.legal.documents.upload.error'));
        } else {
          toast.error(t('admin.legal.documents.upload.error'));
        }
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['workspaceConfigs'] });
      await workspaceConfigsQuery.refetch();

      toast.success(t('admin.legal.documents.upload.success'));
    } catch (error) {
      console.error(error);
      toast.error(t('admin.legal.documents.upload.error'));
    }
  };

  return (
    <div className="flex flex-col space-y-10 grow">
      <div className="space-y-3">
        <h1 className="text-4xl font-bold">{t('admin.legal.documents.title')}</h1>
        <p className="font-light text-muted-foreground">{t('admin.legal.documents.description')}</p>
      </div>

      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">
            {t('admin.legal.documents.section.title')}
          </CardTitle>
        </CardHeader>

        <CardContent className="p-6 pt-0 space-y-10">
          <LegalDocumentBlock
            label={t('admin.legal.documents.regulation.title')}
            description={t('admin.legal.documents.regulation.description')}
            expectedFormat={t('admin.legal.documents.expected.format')}
            currentUrl={regulationUrl}
            registerName="regulation"
            canEdit={canEditLegalDocs}
            onFileChange={handleFileChange}
          />

          <Separator />

          <LegalDocumentBlock
            label={t('admin.legal.documents.privacy.title')}
            description={t('admin.legal.documents.privacy.description')}
            expectedFormat={t('admin.legal.documents.expected.format')}
            currentUrl={privacyPolicyUrl}
            registerName="privacyPolicy"
            canEdit={canEditLegalDocs}
            onFileChange={handleFileChange}
          />

          <Separator />

          <LegalDocumentBlock
            label={t('admin.legal.documents.legal.notice.title')}
            description={t('admin.legal.documents.legal.notice.description')}
            expectedFormat={t('admin.legal.documents.expected.format')}
            currentUrl={legalNoticeUrl}
            registerName="legalNotice"
            canEdit={canEditLegalDocs}
            onFileChange={handleFileChange}
          />
        </CardContent>
      </Card>
    </div>
  );
};
